import React, { useState, useEffect, useMemo } from "react";

// Imports de bibliotecas

import { Container, Row, Col, Tabs, Tab, Card } from "react-bootstrap";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import GaugeChart from 'react-gauge-chart';
import ChartDataLabels from 'chartjs-plugin-datalabels'; // 1. Importe o plugin
import Slider from 'rc-slider';

// Simulação da API - substitua pela sua importação real
import { mat_acumuladas } from "../../services/api";

// Registro dos componentes do Chart.js
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

const DashboardMatriculas = () => {
    // === ESTADOS ===
    const [rawData, setRawData] = useState([]); // Guarda os dados originais da API
    const [dateRange, setDateRange] = useState([1, 1]); // Guarda o período selecionado [inicio, fim]
    const [maxDays, setMaxDays] = useState(1); // Número máximo de dias nos dados
    const [activeTab, setActiveTab] = useState('acumulada'); // Controla a aba ativa

    // === BUSCA DE DADOS ===
    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await mat_acumuladas();
                const days = response.length;
                setRawData(response);
                console.log("Dados recebidos da API:", response);
                setMaxDays(days);
                setDateRange([0, days]); // Padrão: período completo
            } catch (error) {
                console.error("Erro ao buscar dados:", error);
            }
        };
        fetchData();
    }, []);

    // === CÁLCULOS MEMOIZADOS (Performance) ===
    // Filtra os dados brutos com base no período selecionado
    const filteredData = useMemo(() => {
        return rawData.filter(item => {
            const day = parseInt(item.dia);
            return day >= dateRange[0] && day <= dateRange[1];
        });
    }, [rawData, dateRange]);

    // Calcula as médias diárias para o período filtrado (usado nos gauges)
    const periodAverages = useMemo(() => {
        if (filteredData.length === 0) return {};
        const totals = { '2022': 0, '2023': 0, '2024': 0, '2025': 0, '2026': 0, '2027': 0 };
        filteredData.forEach(item => {
            totals['2022'] += parseInt(item.alunos22) || 0;
            totals['2023'] += parseInt(item.alunos23) || 0;
            totals['2024'] += parseInt(item.alunos24) || 0;
            totals['2025'] += parseInt(item.alunos25) || 0;
            totals['2026'] += parseInt(item.alunos26) || 0;
            totals['2027'] += parseInt(item.alunos27) || 0;
        });
        const numberOfDays = filteredData.length;
        const averages = {};
        for (const year in totals) {
            averages[year] = totals[year] / numberOfDays;
        }
        return averages;
    }, [filteredData]);

    // Determina o valor máximo para os gauges (o melhor ano no período)
    const gaugeMax = useMemo(() => {
        const values = Object.values(periodAverages);
        if (values.length === 0) return 1; // Evita divisão por zero
        return Math.max(...values);
    }, [periodAverages]);

    // Prepara os dados para o gráfico de linha (Curva Acumulada)
    const lineChartData = useMemo(() => {
        const labels = filteredData.map(item => `Dia ${item.dia}`);
        let acumulador = { '2022': 0, '2023': 0, '2024': 0, '2025': 0, '2026': 0, '2027': 0 };
        const yearData = { '2022': [], '2023': [], '2024': [], '2025': [], '2026': [], '2027': [] };

        filteredData.forEach(item => {
            Object.keys(acumulador).forEach(year => {
                acumulador[year] += parseInt(item[`alunos${year.slice(2)}`]) || 0;
                yearData[year].push(acumulador[year]);
            });
        });

        return {
            labels,
            datasets: [
                { label: '2022', data: yearData['2022'], borderColor: '#ef4444', backgroundColor: '#ef4444' },
                { label: '2023', data: yearData['2023'], borderColor: '#3b82f6', backgroundColor: '#3b82f6' },
                { label: '2024', data: yearData['2024'], borderColor: '#10b981', backgroundColor: '#10b981' },
                { label: '2025', data: yearData['2025'], borderColor: '#eab308', backgroundColor: '#eab308' },
                { label: '2026', data: yearData['2026'], borderColor: '#8b5cf6', backgroundColor: '#8b5cf6' },
                { label: '2027', data: yearData['2027'], borderColor: '#000000', backgroundColor: '#000000' },
            ]
        };
    }, [filteredData]);

    // Prepara os dados para o gráfico de barras (Matrículas por Dia)
    const barChartData = useMemo(() => {
        const labels = filteredData.map(item => `Dia ${item.dia}`);
        return {
            labels,
            datasets: [
                { label: '2022', data: filteredData.map(d => parseInt(d.alunos22) || 0), backgroundColor: '#ef4444' },
                { label: '2023', data: filteredData.map(d => parseInt(d.alunos23) || 0), backgroundColor: '#3b82f6' },
                { label: '2024', data: filteredData.map(d => parseInt(d.alunos24) || 0), backgroundColor: '#10b981' },
                { label: '2025', data: filteredData.map(d => parseInt(d.alunos25) || 0), backgroundColor: '#eab308' },
                { label: '2026', data: filteredData.map(d => parseInt(d.alunos26) || 0), backgroundColor: '#8b5cf6' },
                { label: '2027', data: filteredData.map(d => parseInt(d.alunos27) || 0), backgroundColor: '#000000' },
            ]
        };
    }, [filteredData]);


    // === OPÇÕES DOS GRÁFICOS ===
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: 'top' } },
        scales: { x: { ticks: { maxTicksLimit: 15 } } } // Limita o número de labels no eixo X para não poluir
    };

    if (rawData.length === 0) {
        return <Container><p>Carregando dados...</p></Container>;
    }

    // === RENDERIZAÇÃO ===
    return (
        <Container fluid className="p-4">
            <Row className="mb-4">
                <Col md={12}>
                    <Card className="bg-light border-info">
                        <Card.Body>
                            <Card.Title className="text-info">
                                <i className="fas fa-info-circle"></i> Informação sobre a Curva de Crescimento
                            </Card.Title>
                            <p className="mb-0">
                                A curva de crescimento apresentada é baseada em <strong>dias de matrículas</strong>, não em datas do calendário.
                                Por isso, pode haver diferenças entre a quantidade de matrículas da curva acumulada com a quantidade de matrículas
                                no ano anterior na mesma data. Cada ano pode ter períodos de abertura de inscrições em datas diferentes,
                                resultando em progressões distintas ao longo do tempo.
                            </p>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
            <Row>
                {/* --- COLUNA ESQUERDA: CONTROLES E GAUGES --- */}
                <Col md={3}>
                    <Card>
                        <Card.Body>
                            <Card.Title>Painel de Controle</Card.Title>
                            <hr />
                            <h6>Período de Análise</h6>
                            <div className="px-2">
                                <Slider
                                    range
                                    min={1}
                                    max={maxDays}
                                    value={dateRange}
                                    onChange={setDateRange}
                                    allowCross={false}
                                />
                            </div>
                            <div className="d-flex justify-content-between mt-2">
                                <span>Dia {dateRange[0]}</span>
                                <span>Dia {dateRange[1]}</span>
                            </div>
                        </Card.Body>
                    </Card>
                    <Card className="mt-4">
                        <Card.Body>
                            <Card.Title>Média Diária no Período</Card.Title>
                            <Card.Subtitle className="mb-2 text-muted">Matriculas por dia</Card.Subtitle>
                            <hr />
                            <Row>
                                {Object.entries(periodAverages).map(([year, avg]) => (
                                    <Col key={year} xs={12} sm={6} lg={12} xl={6} className="text-center mb-3">
                                        <strong>Ano {year}</strong>
                                        {/* A div com maxWidth ainda é útil para manter um tamanho consistente */}
                                        <div style={{ maxWidth: '180px', margin: '0 auto' }}>
                                            <GaugeChart
                                                id={`gauge-${year}`}
                                                nrOfLevels={20}
                                                colors={['#ef4444', '#f59e0b', '#10b981']}
                                                arcWidth={0.3}
                                                percent={gaugeMax > 0 ? avg / gaugeMax : 0}
                                                textColor="#343a40"
                                                formatTextValue={() => avg.toFixed(1)}
                                            />
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>

                {/* --- COLUNA DIREITA: GRÁFICOS PRINCIPAIS --- */}
                <Col md={9}>
                    <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} id="graficos-principais-tabs" className="mb-3">
                        <Tab eventKey="acumulada" title="Curva Acumulada">
                            <div style={{ height: '65vh' }}>
                                <Line options={chartOptions} data={lineChartData} />
                            </div>
                        </Tab>
                        <Tab eventKey="por_dia" title="Matrículas por Dia">
                            <div style={{ height: '65vh' }}>
                                <Bar options={{ ...chartOptions, scales: { ...chartOptions.scales, x: { ...chartOptions.scales.x, stacked: false }, y: { stacked: false } } }} data={barChartData} />
                            </div>
                        </Tab>
                    </Tabs>
                </Col>
            </Row>
        </Container>
    );
};

export default DashboardMatriculas;